import { HOUR_MS } from '../kpowFormat';

export type KpowDisplayParty = 'D' | 'R' | 'Even';

/** Symmetric display + numeric envelope for the composite and per-component scores. */
export interface KpowScore {
    signed_value: number;
    display_party: KpowDisplayParty;
    display_value: number;
}

export interface KpowComponent {
    name: string;
    /** Null when the component was unavailable at snapshot time. */
    score: KpowScore | null;
    weight: number;
}

/** Response shape of GET /v1/cached/indexes/KPOW. */
export interface KpowSnapshot {
    /** Unix seconds. */
    data_as_of: number;
    composite: KpowScore;
    components: KpowComponent[];
}

/** History points carry the same display envelope as the composite snapshot, plus a timestamp. */
export interface KpowHistoryPoint extends KpowScore {
    /** Unix seconds. */
    end_period_ts: number;
}

/** Response shape of GET /v1/cached/indexes/KPOW/history. */
export interface KpowHistory {
    points: KpowHistoryPoint[];
}

/**
 * Client calls hit our same-origin edge, which rewrites to
 * `https://api.elections.kalshi.com/v1/cached/indexes/KPOW` (see
 * `rewrites()` in `next.config.js`). This sidesteps the CORS + AWS-WAF
 * challenge restrictions that the prod API enforces against non-kalshi.com
 * origins, so preview deploys + local dev can load real data without
 * special config.
 */
const KPOW_BASE = '/api/kpow';

/** Hourly history window requested for the chart; stays under the API's 5000-hour cap. */
const HISTORY_WINDOW_MS = 180 * 24 * HOUR_MS;

async function fetchKpow<T>(path: string): Promise<T> {
    // Calling our own edge (the rewrite proxies to api.elections.kalshi.com),
    // so the AWS-WAF challenge wrapper isn't needed and would add a no-op token fetch.
    // eslint-disable-next-line no-restricted-globals
    const res = await fetch(path);
    if (!res.ok) throw new Error(`KPOW API error ${res.status} for ${path}`);
    return res.json() as Promise<T>;
}

export async function fetchKpowSnapshot(): Promise<KpowSnapshot> {
    return fetchKpow<KpowSnapshot>(KPOW_BASE);
}

export async function fetchKpowHistory(): Promise<KpowHistory> {
    // Floor the lower bound to the hour so the request URL is stable across reads.
    const startTs = (Math.floor((Date.now() - HISTORY_WINDOW_MS) / HOUR_MS) * HOUR_MS) / 1000;
    return fetchKpow<KpowHistory>(`${KPOW_BASE}/history?start_ts=${startTs}`);
}
